import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Users, Clock, GraduationCap } from "lucide-react";
import { TRAINING_PROGRAMS } from "@/lib/data";
import { useSEO } from "@/hooks/useSEO";

export default function TrainingDetail() {
  const { slug } = useParams();
  const program = TRAINING_PROGRAMS.find((p) => p.slug === slug);

  useSEO({
    title: program ? `${program.title} | EnPrex Corporate Training` : "Corporate Training | EnPrex Services",
    description: program?.short || "Corporate training for enterprise energy and commodity trading teams.",
    path: `/training/${slug || ""}`,
  });

  if (!program) {
    return (
      <div className="container-x py-32 text-center" data-testid="program-not-found">
        <h1 className="font-display text-4xl text-white">Program not found</h1>
        <Link to="/training" className="mt-6 inline-block text-[#D4AF37] underline">← Back to Training & Academy</Link>
      </div>
    );
  }

  const Icon = program.icon;

  return (
    <article data-testid="training-detail" className="pb-24">
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F3A]/70 to-[#050A14]" />
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#D4AF37]/[0.06] blur-[140px]" />
        <div className="container-x relative pt-16 pb-20">
          <Link to="/training" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 hover:text-[#D4AF37] font-mono mb-10" data-testid="back-to-training">
            <ArrowLeft size={14} /> All Programs
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <Icon className="text-[#D4AF37] mb-8" size={44} strokeWidth={1.2} />
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-4 font-mono">— Training & Academy</div>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-light text-white tracking-tighter leading-[0.98] max-w-5xl">
              {program.title}
            </h1>
            <p className="mt-8 text-lg md:text-xl text-slate-300 max-w-3xl font-light leading-relaxed">
              {program.short}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-slate-400">
              <div className="flex items-center gap-2"><GraduationCap size={14} className="text-[#D4AF37]" /> {program.format}</div>
              <div className="flex items-center gap-2"><Users size={14} className="text-[#D4AF37]" /> {program.audience}</div>
              <div className="flex items-center gap-2"><Clock size={14} className="text-[#D4AF37]" /> {program.duration}</div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="container-x py-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono">01 — Overview</div>
        </div>
        <div className="lg:col-span-8">
          <p className="text-xl md:text-2xl text-white font-light leading-relaxed tracking-tight">
            {program.overview}
          </p>
        </div>
      </section>

      <section className="border-t border-white/5 bg-[#0B1F3A]/30">
        <div className="container-x py-20 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono mb-6">02 — Curriculum</div>
            <h2 className="font-display text-3xl md:text-4xl text-white font-light tracking-tight leading-tight">
              What we cover.
            </h2>
          </div>
          <div className="lg:col-span-8 grid md:grid-cols-2 gap-4">
            {program.modules.map((m, i) => (
              <div key={i} className="border border-white/5 bg-white/[0.02] p-6 hover:border-[#D4AF37]/30 transition-colors">
                <div className="font-mono text-xs text-[#D4AF37] mb-2">{String(i + 1).padStart(2, "0")}</div>
                <p className="text-slate-200 leading-relaxed">{m}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x py-20 border-t border-white/5 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#D4AF37] font-mono mb-6">03 — Outcomes</div>
          <h2 className="font-display text-3xl md:text-4xl text-white font-light tracking-tight leading-tight">
            What participants take away.
          </h2>
        </div>
        <div className="lg:col-span-8">
          <ul className="space-y-4">
            {program.outcomes.map((o, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 className="text-[#D4AF37] mt-1 flex-shrink-0" size={18} />
                <span className="text-slate-300 leading-relaxed">{o}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="container-x py-20">
        <div className="border border-[#D4AF37]/25 bg-gradient-to-br from-[#0B1F3A] to-[#050A14] p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h2 className="font-display text-2xl md:text-4xl font-light text-white tracking-tight leading-tight max-w-2xl">
              Bring this program to your organization.
            </h2>
            <p className="mt-4 text-slate-400 max-w-2xl">
              All enquiries are managed exclusively through our secure enquiry form. Our academy lead will respond within two business days with a tailored proposal.
            </p>
          </div>
          <Link to="/contact" data-testid="training-cta-request" className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#0B1F3A] px-8 py-4 text-sm font-semibold uppercase tracking-[0.15em] hover:bg-[#FDE047] transition-colors whitespace-nowrap">
            Request Corporate Training <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </article>
  );
}
